<template>
    <div class="container url-ranking-report">
        <div v-if="isDataFetched" 
            class="url-ranking-report__advanced">
            <advanced-filters :options="advancedOptions" 
                :predefinedOptions="_advancedFilters" 
                copyPage
                exportEnabled
                @share="copyFilters" 
                @export="exportCsv"
                @apply-advanced="applyAdvancedOptions" />
        </div>
        <div class="url-ranking-report__loader" 
            v-if="!isDataFetched">
            <base-loader class="url-ranking-report__spinner" />
        </div>
        <div v-else>
            <div class="common-table url-ranking-report__table">
                <div class="url-ranking-report__extra-filters">
                    <div class="url-ranking-report__zones-holder url-ranking-report__extra-filters-container">
                        <base-check-box v-for="zone in checkboxes" 
                            :key="zone.value" 
                            :label="zone.value"
                            v-model="zone.checked" 
                            :checkbox-color="zone.color" />
                    </div>
                    <div class="url-ranking-report__table-type-holder url-ranking-report__extra-filters-container">
                        <base-radio v-model="mapField" 
                            value="msv" 
                            label="MSV" 
                            crossColor="#8F90A6" />
                        <base-radio v-model="mapField" 
                            value="keyword" 
                            label="# KW" 
                            crossColor="#8F90A6" />
                    </div>
                </div>
                <div class="common-table__date_selection common-table__row">
                    <div class="url-ranking-report__radio-wrap">
                        <base-radio class="url__radio" 
                            label="Mapped" 
                            v-model="filterParams.type" 
                            value="1" />
                        <base-radio class="url__radio" 
                            label="Ranked" 
                            v-model="filterParams.type" 
                            value="2" />
                    </div>
                    <base-select v-if="filterParams.type === '2'" 
                        class="url-ranking-report__web-property"
                        style="margin-left: 9px" 
                        truncateLabel 
                        :titleStyle="{ color: '#011627', paddingLeft: '15px' }"
                        :options="webPropertyGroups" 
                        v-model="filterParams.webPropertyGroupId" 
                        showTopBorder
                        :propListStyle="{ maxHeight: '360px', maxWidth: '200px' }" 
                        placeholder="Web Property" />

                    <base-select class="url-ranking-report__grid-select" 
                        style="margin-left: auto;"
                        :options="paginationOptions" 
                        alignLeft 
                        :titleStyle="{ 'padding-left': '5px' }" 
                        :headerStyle="{}"
                        showTopBorder 
                        shiftLabelRight 
                        v-model="pageItemsLimit" 
                        @change="handleLimitChange" />
                </div>
                <div class="common-table__row common-table__summary" 
                    v-if="summary">
                    <div class="common-table__summary-cell" 
                        v-for="column in tableHeaderCells" 
                        :key="column.property"
                        :class="[`url-ranking-report__${column.property}`]">
                        <div class="url-ranking-report__summary"
                            :class="[`url-ranking-report__summary_${column.property}`]" 
                            v-if="column.property === 'changePercent'" >
                            {{ summary[column.property] | commaDividedNumber }}%
                        </div>
                        <div v-else
                            class="url-ranking-report__summary"
                            :class="[`url-ranking-report__summary_${column.property}`]" >
                            {{ summary[column.property] | commaDividedNumber }}
                        </div>
                    </div>
                </div>
                <div class="common-table__row common-table__header">
                    <div class="common-table__header-cell" 
                        v-for="column in tableHeaderCells" 
                        :key="column.property"
                        :class="[`url-ranking-report__${column.property}`]">
                        <base-multi-select-sortable-header v-if="column.property !== 'label'"
                            :column="column"
                            :predefinedNumberFilter="predefinedColumnFilters[column.property]"
                            :sortConfig="sortConfig"
                            @sort="handleSortingChange(column, $event)"
                            @apply="applyColumnFilter($event, column)" />
                        <base-table-sortable-column v-else
                            class="url-ranking-report__sortable-header"
                            :class="[`url-ranking-report__sortable-header_${column.property}`]" 
                            :label="column.label"
                            :prop-name="column.property" 
                            :sort-config="sortConfig"
                            @click.native="onHeaderCellClicked(column)" />
                        <base-select v-if="column.property === 'ranking1'" 
                            class="url__select" 
                            :class="{'url__select_shifted': !!summary}"
                            :options="formattedSnapshots"
                            alignLeft 
                            :titleStyle="{ 'padding-left': '5px' }" 
                            :headerStyle="{}" 
                            showTopBorder
                            shiftLabelRight 
                            v-model="selectedSnapshots.snapshot1" />
                        <base-select v-if="column.property === 'ranking2'" 
                            class="url__select" 
                            :class="{'url__select_shifted': !!summary}"
                            :options="formattedSnapshots"
                            alignLeft 
                            :titleStyle="{ 'padding-left': '5px' }" 
                            :headerStyle="{}" 
                            showTopBorder
                            shiftLabelRight 
                            v-model="selectedSnapshots.snapshot2" />
                    </div>
                </div>
                <template v-if="dataMissing">
                    <div class="common-table__row no-data-block">
                        No records found
                    </div>
                </template>
                <template v-else>
                    <template v-for="url in tableData">
                        <div :key="url.label" 
                            class="common-table__row">
                            <div class="common-table__cell" 
                                v-for="column in tableHeaderCells" 
                                :key="column.property"
                                :class="[`url-ranking-report__${column.property}`, `url-ranking-report__row_${column.property}`]">
                                <template v-if="column.property === 'label'">
                                    <div class="url-ranking-report__label">
                                        <a v-if="filterParams.type === '1'"
                                            target="_blank"
                                            :href="url.contentBriefUrl">
                                            <base-material-design-icon class="url-ranking-report__label-icon"
                                            icon="file" />
                                        </a>
                                        <a :href="url[column.property]" 
                                            class="url-ranking-report__label-link"
                                            target="_blank" 
                                            :title="url[column.property]">
                                            <base-truncate-label :label="url[column.property]"
                                                watchClass="url-ranking-report__label-link" />
                                        </a>
                                    </div>
                                </template>
                                <template v-else-if="['change', 'changePercent'].includes(column.property)">
                                    <template v-if="url[column.property] === 0">
                                        No change
                                    </template>
                                    <template v-else>
                                        <span :class="getChangeCssClass(url[column.property])">
                                            {{ url[column.property] | commaDividedNumber }}<template
                                                v-if="column.property === 'changePercent'">%</template>
                                        </span>
                                    </template>
                                </template>
                                <template v-else>
                                    {{ url[column.property] | commaDividedNumber }}
                                </template>
                            </div>
                        </div>
                    </template>
                </template>
            </div>

            <div class="pagination-wrapper" 
                v-show="hasPagination">
                <base-pagination :limit="pageItemsLimit" 
                    :total-items="mappedUrls.length"
                    @pageChanged="setPaginationProps" />
            </div>
            <div class="url-ranking-report__update-loader" 
                v-if="isDataUpdating">
                <base-loader class="url-ranking-report__update-loader-ring" />
            </div>
        </div>
    </div>
</template>

<script>
import _ from 'lodash'
import { baseComponent } from '@/features/reports/common/Mixins'
import BaseSortMixins from '@/common/BaseSortMixins'
import { mapActions, mapState } from 'vuex'
import filters from '@/common/Filters'
import helper from '@/services/Helper'
import ToasterService from '@/services/toasterService'
import {
    addOptionsToConfig,
    ADVANCED_OPTION_TYPE,
    convertAdvancedOptionsToFilters,
    urlFieldsConfig,
} from '@/features/reports/common/advanced-options/config'
import AdvancedFilters from '@/features/reports/common/advanced-filters/AdvancedFilters'
import { advancedFiltersMixins } from '@/features/reports/common/AdvancedFiltersMixins'
import csvExport from '@/services/CSVExport'

const DEFAULT_ITEMS_PER_PAGE = 100

const DEFAULT_TABLE_CONFIG = [
    {
        label: 'URL',
        property: 'label',
        propName: 'label',
    },
    {
        label: '# of KWS',
        property: 'keywordTotal',
        propName: 'keywordTotal',
        isNumber: true,
    },
    {
        label: 'MSV',
        property: 'totalMsv',
        propName: 'totalMsv',
        isNumber: true,
    },
    {
        label: 'Ranking on',
        property: 'ranking1',
        propName: 'ranking1',
        isNumber: true,
    },
    {
        label: 'Ranking on',
        property: 'ranking2',
        propName: 'ranking2',
        isNumber: true,
    },
    {
        label: 'Change',
        property: 'change',
        propName: 'change',
        isNumber: true,
    },
    {
        label: 'Change %',
        property: 'changePercent',
        propName: 'changePercent',
        isNumber: true,
    }
]

const ADVANCED_OPTIONS_CONFIG = urlFieldsConfig

const EMPTY_FILTERS = {
    type: '1',
    webPropertyGroupId: null,
}

export default {
    name: 'UrlRankingReport',
    mixins: [baseComponent, BaseSortMixins, advancedFiltersMixins],
    filters: {
        ...filters
    },
    components: { AdvancedFilters },
    props: ['filters'],
    data() {
        return {
            filterParams: _.cloneDeep(EMPTY_FILTERS),
            selectedAdvancedOptions: [],
            selectedSnapshots: {
                snapshot1: null,
                snapshot2: null,
            },
            mapField: 'keyword',
            pageItemsLimit: DEFAULT_ITEMS_PER_PAGE,
            formattedSnapshots: [],
            paginationProps: { from: 0, to: DEFAULT_ITEMS_PER_PAGE },
            sortConfig: [{
                sortDirection: helper.DIRECTION_DESC,
                propertyName: 'change'
            }],
            paginationOptions: [
                {
                    label: '100',
                    value: 100
                },
                {
                    label: '250',
                    value: 250
                },
                {
                    label: '500',
                    value: 500
                }
            ],
            checkboxes: [
                {
                    value: '01 - 03',
                    color: '#26a303',
                    checked: true
                },
                {
                    value: '04 - 07',
                    color: '#0be129',
                    checked: true
                },
                {
                    value: '08 - 10',
                    color: '#37f7ae',
                    checked: true
                },
                {
                    value: '11 - 25',
                    color: '#49b6e6',
                    checked: false
                },
                {
                    value: '26 - 50',
                    color: '#284aff',
                    checked: false
                },
                {
                    value: '51 - 100',
                    color: '#9013fe',
                    checked: false
                },
            ],
            predefinedColumnFilters: {
                keywordTotal: {
                    operator: null,
                    value: null
                },
                totalMsv: {
                    operator: null,
                    value: null
                },
                ranking1: {
                    operator: null,
                    value: null
                },
                ranking2: {
                    operator: null,
                    value: null
                },
                change: {
                    operator: null,
                    value: null
                },
                changePercent: {
                    operator: null,
                    value: null
                }
            },
            appliedColumnFilters: [],
            advancedOptionsConfig: ADVANCED_OPTIONS_CONFIG,
            tableHeaderCells: DEFAULT_TABLE_CONFIG
        }
    },
    created() {
        if (this.snapshotsPresent) {
            this.mapSnapshots();
        }
    },
    computed: {
        ...mapState('urlRankingReport', [
            'mappedUrls'
        ]),
        ...mapState('rankZoneCommon', {
            advancedOptions(state) {
                let config = _.cloneDeep(this.advancedOptionsConfig)
                addOptionsToConfig(config, state.sections, ADVANCED_OPTION_TYPE.SECTION)
                addOptionsToConfig(config, state.attributes, ADVANCED_OPTION_TYPE.ATTRIBUTE)
                addOptionsToConfig(config, state.urlStatuses, ADVANCED_OPTION_TYPE.URL_STATUS)
                addOptionsToConfig(config, state.pageTypes, ADVANCED_OPTION_TYPE.PAGE_TYPE)
                addOptionsToConfig(config, state.scores, ADVANCED_OPTION_TYPE.SCORE)
                addOptionsToConfig(config, state.isOptimized, ADVANCED_OPTION_TYPE.IS_OPTIMIZED)
                addOptionsToConfig(config, state.urlAttributes, ADVANCED_OPTION_TYPE.URL_ATTRIBUTE)
                addOptionsToConfig(config, state.serpFeature, ADVANCED_OPTION_TYPE.SERP_FEATURE)
                addOptionsToConfig(config, state.contentBriefStatuses, ADVANCED_OPTION_TYPE.CONTENT_BRIEF_STATUS)
                addOptionsToConfig(config, state.mappedUrls, ADVANCED_OPTION_TYPE.URL)
                addOptionsToConfig(config, state.mismatchedUrls, ADVANCED_OPTION_TYPE.MISMATCHED_URLS)
                
                return state.difficultyScoreEnabled ? config : config.filter(i => i.type !== ADVANCED_OPTION_TYPE.DIFF_SCORE)
            },
            webPropertyGroups(state) {
                return _.cloneDeep(state.webPropertyGroups).sort((a, b) => {
                    if ((a.type === 'Clients' && b.type === 'Siblings') || (a.type === 'Siblings' && b.type === 'Competitors')) {
                        return -1
                    } else if ((b.type === 'Clients' && a.type === 'Siblings') || (b.type === 'Siblings' && a.type === 'Competitors')) {
                        return 1
                    } else {
                        return 0
                    }
                }).map(item => {
                    return {
                        value: item._id,
                        label: item.label,
                        type: item.type,
                        style: {
                            color: item.type === 'Siblings' ? '#284aff' : '#011627',
                            fontWeight: item.type === 'Clients' ? 'bold' : 'normal'
                        },
                        attributes: item.type === 'Clients' ? [{ 'private-content': true }] : [],
                    }
                }) || []
            },
        }),
        summary() {
            if (!this.mappedUrls || !this.mappedUrls.length) {
                return null
            }
            const emptySummaryObject = {}
            const keysToSkip = ['contentBriefUrl', 'label', 'change', 'changePercent'];
            const keys = Object.keys(this.mappedUrls[0]).filter(key => !keysToSkip.includes(key));
            keys.forEach(key => emptySummaryObject[key] = 0);
            const summaryData = this.mappedUrls.reduce((acc, item) => {
                keys.forEach(key => acc[key] += item[key]);

                return acc;
            }, emptySummaryObject);
            summaryData.change = summaryData.ranking1 - summaryData.ranking2;
            let changePercent;
            if (summaryData.ranking2 === 0) {
                changePercent = summaryData.ranking1 === 0 ? 0 : 100
            } else {
                changePercent = (summaryData.ranking1 - summaryData.ranking2) * 100 / summaryData.ranking2
            }
            summaryData.changePercent = Math.round(changePercent);

            summaryData.label = 'Filter summary';

            return summaryData
        },
        sortedData() {
            if (!this.sortConfig.length) {
                return this.mappedUrls
            }

            function sortData(data, param, direction) {
                helper.sortByParamMutation(data, param, direction)
                data.forEach(item => {
                    if (item.children && item.children.length) {
                        sortData(item.children, param, direction)
                    }
                })
            }
            const { propertyName, sortDirection } = this.sortConfig[0]
            const sortedData = _.cloneDeep(this.mappedUrls)
            sortData(sortedData, propertyName, sortDirection)

            return sortedData
        },
        tableData() {
            return this.sortedData.slice(this.paginationProps.from, this.paginationProps.to)
        },
        dataMissing() {
            const isDataMissing = !this.mappedUrls.length
            return isDataMissing
        },
        hasPagination() {
            return this.mappedUrls.length > this.pageItemsLimit
        },
        rankZones() {
            return this.checkboxes.filter(item => item.checked)
                .map(item => item.value);
        },
    },
    watch: {
        snapshots: {
            handler() {
                if (!this.snapshotsPresent) {
                    return;
                }
                this.selectedSnapshots = {
                    snapshot1: this.snapshots[0],
                    snapshot2: this.snapshots[1]
                }
            },
            deep: true,
            immediate: true
        },
        selectedSnapshots: {
            handler() {
                this.isDataFetched && this.updateData()
            },
            deep: true,
            immediate: true
        },
        webPropertyGroups: {
            handler(groups) {
                if (groups.length) {
                    /* set default webPropertyGroup */
                    this.filterParams.webPropertyGroupId = groups[0].value
                }
            },
            deep: true,
            immediate: true
        },
        'filterParams.type'(v) {
            if (v === '1') {
                this.filterParams.webPropertyGroupId = this.webPropertyGroups[0].value
            }
        },
        filterParams: {
            handler() {
                this.$nextTick(() => {
                    this.isDataFetched && this.updateData()
                });
            },
            deep: true,
            immediate: true
        },
        rankZones() {
            this.handleCalculationFieldsChange()
        },
        mapField() {
            this.handleCalculationFieldsChange()
        },
        mappedUrls() {
            this.setPaginationProps(this.paginationProps)
        },
        selectedAdvancedOptions: {
            async handler() {
                this.isDataFetched && this.updateData()
            },
            deep: true
        },
    },
    methods: {
        ...mapActions('urlRankingReport', ['getUrlData', 'recalculateData']),
        mapSnapshots() {
            this.selectedSnapshots = {
                snapshot1: this.snapshots[0],
                snapshot2: this.snapshots[1]
            }
            this.formattedSnapshots = this.snapshots.map(snapshot => {
                return {
                    label: this.$options.filters.formattedDate(snapshot),
                    value: snapshot
                }
            })

            this.$nextTick(this.loadPageData)
        },
        async loadPageData() {
            this.setupFilterParams();
            const advancedFilters = convertAdvancedOptionsToFilters(this.selectedAdvancedOptions)
            const filters = Object.assign({}, this.filterParams, { advancedFilters });
            filters.rankZones = this.rankZones;
            filters.mapByKeyword = this.mapField === 'keyword'
            filters.columnFilters = this.appliedColumnFilters.filter(filter => !!filter.value && !!filter.value)
            const { snapshot1, snapshot2 } = this.selectedSnapshots
            const data = await this.getUrlData({
                clientId: this.clientId, filters: {
                    ...filters,
                    snapshot1,
                    snapshot2
                }
            })
            data && (this.isDataFetched = true)
        },
        async updateData() {
            const advancedFilters = convertAdvancedOptionsToFilters(this.selectedAdvancedOptions)

            const filters = Object.assign({}, this.filterParams, { advancedFilters });
            filters.rankZones = this.rankZones;
            filters.mapByKeyword = this.mapField === 'keyword';
            filters.columnFilters = this.appliedColumnFilters.filter(filter => !!filter.value && !!filter.value);
            if (this.isDataFetched) {
                this.isDataUpdating = true
            }
            const { snapshot1, snapshot2 } = this.selectedSnapshots
            await this.getUrlData({
                clientId: this.clientId, filters: {
                    ...filters,
                    snapshot1,
                    snapshot2
                }
            })
            this.isDataFetched = true
            this.isDataUpdating = false
        },
        handleLimitChange(value) {
            this.pageItemsLimit = value;
            this.paginationProps = {
                from: 0,
                to: value
            };
        },
        setPaginationProps(props) {
            this.paginationProps = props;
        },
        handleCalculationFieldsChange() {
            const columnFilters = this.appliedColumnFilters.filter(filter => !!filter.value && !!filter.value)
            
            this.recalculateData({
                rankZones: this.rankZones,
                mapByKeyword: this.mapField === 'keyword',
                columnFilters
            });
        },
        getChangeCssClass(value) {
            if (value > 0) {
                return 'url-ranking-report__change_uptrend'
            }

            if (value < 0) {
                return 'url-ranking-report__change_downtrend'
            }

            return ''
        },
        async applyAdvancedOptions(options) {
            this.selectedAdvancedOptions = _.cloneDeep(options)
        },
        handleSortingChange(column, direction) {
            if (direction === 'clear') {
                this.sortConfig = [{
                    sortDirection: helper.DIRECTION_DESC,
                    propertyName: 'change'
                }];

                return;
            }
            
            this.sortConfig = [{
                sortDirection: direction,
                propertyName: column.property
            }];
        },
        applyColumnFilter({ value, operator }, { property }) {
            if (operator === null) {
                this.appliedColumnFilters = this.appliedColumnFilters.filter(filter => filter.property !== property);
                this.handleCalculationFieldsChange()
                return;
            }

            const targetFilterIndex = this.appliedColumnFilters.findIndex(filter => filter.property === property);

            if (targetFilterIndex === -1) {
                this.appliedColumnFilters.push({
                    property,
                    value,
                    operator
                });
                this.handleCalculationFieldsChange()
                return
            }

            this.appliedColumnFilters[targetFilterIndex] = {
                property,
                operator,
                value
            }
            this.handleCalculationFieldsChange()
        },
        copyFilters() {
            let path = `${window.location.origin}${window.location.pathname}#${this.$route.path}?filters=`
            const copiedAdvancedFilters = []
            this.selectedAdvancedOptions.forEach(item => {
                copiedAdvancedFilters.push({
                    selectedField: item.selectedField,
                    selectedFilter: item.selectedFilter,
                    selectedOperator: item.selectedOperator
                })
            })
            const copiedFilters = {
                client_id: this.clientId,
                filters: copiedAdvancedFilters,
                filterParams: {},
                sortConfig: this.sortConfig,
                pageItemsLimit: this.pageItemsLimit
            }
            Object.entries(this.filterParams).forEach(([key, value]) => {
                if (_.isObject(value) && Object.values(value).some(item => item)) {
                    copiedFilters.filterParams[key] = _.cloneDeep(value)
                }
                if (_.isInteger(value) || (_.isString(value) && Boolean(value))) {
                    copiedFilters.filterParams[key] = value
                }
            })
            copiedFilters.mapField = this.mapField
            copiedFilters.selectedSnapshots = this.selectedSnapshots
            copiedFilters.rankZones = this.checkboxes
                .filter(item => item.checked)
                .map(item => item.value)
            copiedFilters.appliedColumnFilters = this.appliedColumnFilters
            path += JSON.stringify(copiedFilters)
            path = encodeURI(path)
            this.$copyText(path)
                .then(
                    () => {
                        ToasterService.showToast(ToasterService.INFO_TYPE, 'Filters copied to clipboard')
                    },
                    // eslint-disable-next-line handle-callback-err
                    () => {
                        ToasterService.showToast(ToasterService.ERROR_TYPE, 'Can not copy filters')
                    }
                )
        },
        setupFilterParams() {
            if (!this.filters) {
                return;
            }

            let filters = null
            try {
                filters = JSON.parse(this.filters)
            } catch (e) {
                return
            }
            const clientId = filters.client_id
            const emptyUrlFilters = !Object.values(filters).some(item => item)
            if (emptyUrlFilters || this.isQueryFiltersApplied || !clientId) {
                return
            }
            // we need to change client, return false
            if (clientId !== this.clientId) {
                if (!this.setNewClient(clientId, true)) {
                    // user does not allowed to see this client, filters will be ignored
                    console.warn(this.$route.name + ' client with id ', clientId, 'does not connected to current user')
                    this.isQueryFiltersApplied = true
                    // this.filterParams = { ...this.filterParams, ..._.cloneDeep(EMPTY_FILTERS) }
                }
                return
            }
            // there is exactly the same client selected
            try {
                if (filters.filters.length) {
                    this.advancedFilters = JSON.stringify({filters: filters.filters, client_id: filters.client_id});
                    this.restoreAdvancedFilters();
                }
            } catch (e) {
                console.error('Error to set advanced filters')
            }
            try {
                if (filters.filterParams) {
                    this.filterParams = Object.assign({}, EMPTY_FILTERS, filters.filterParams)
                }
            } catch (e) {
                console.error('Error to set filter params filters')
            }
            try {
                if (filters.sortConfig) {
                    this.sortConfig = [...filters.sortConfig]
                }
            } catch (e) {
                console.error('Error to set sort config')
            }
            try {
                if (filters.pageItemsLimit) {
                    this.pageItemsLimit = filters.pageItemsLimit
                }
            } catch (e) {
                console.error('Error to set page items limit')
            }

            if (filters.selectedSnapshots) {
                this.selectedSnapshots = { ...filters.selectedSnapshots };
            }

            if (filters.rankZones) {
                this.checkboxes.forEach(item => item.checked = filters.rankZones.includes(item.value))
            }

            if (filters.mapField) {
                this.mapField = filters.mapField
            }

            if (filters.appliedColumnFilters) {
                filters.appliedColumnFilters.forEach(filter => {
                    this.predefinedColumnFilters[filter.property] = {
                        operator: filter.operator,
                        value: filter.value
                    }
                })
                this.appliedColumnFilters = filters.appliedColumnFilters
            }
            this.isQueryFiltersApplied = true
        },
        exportCsv() {
            let data = [];
            const headerCells = _.cloneDeep(this.tableHeaderCells.map(cell => cell.label));
            const snapshot1Index = headerCells.findIndex(cell => cell === 'Ranking on');
            headerCells[snapshot1Index] = `Rank Date ${moment(this.selectedSnapshots.snapshot1).format('YYYY/MM/DD')}`
            headerCells[snapshot1Index + 1] = `Rank Date ${moment(this.selectedSnapshots.snapshot2).format('YYYY/MM/DD')}`
            if (this.filterParams.type === '1') {
                headerCells.push('Content Brief Url')
            }
            data.push(headerCells);
            const dataToExport = this.sortedData.map(item => {
                const mappedItem = []

                this.tableHeaderCells.forEach(column => {
                    if (['change', 'changePercent'].includes(column.property)) {
                        if (item[column.property] === 0) {
                            mappedItem.push('No change')
                        } else {
                            let value = item[column.property]
                            if (column.property === 'changePercent') {
                                value = value + '%'
                            }
                            mappedItem.push(value)
                        }
                    } else {
                        mappedItem.push(item[column.property])
                    }
                })

                if (this.filterParams.type === '1') {
                    mappedItem.push(item.contentBriefUrl)
                }

                return mappedItem
            })

            data = [...data, ...dataToExport]

            csvExport(`${this.selectedClient.name.replace(/ /g, '-')}_Url-Rank-Report_${moment().format('YYYY-MM-DD')}`, data)
        }
    }
}
</script>

<style scoped lang="scss">
.url-ranking-report {
    min-width: 1200px;
    position: relative;
    min-height: 200vh !important;

    &__extra-filters {
        display: flex;
        gap: 14px;
        margin-bottom: 14px;
    }

    &__extra-filters-container {
        border: 1px solid $rfx-border-color;
        height: 60px;
        padding: 0 24px;
        align-items: center;
        display: flex;
        gap: 28px;
        background-color: #fff;
    }

    &__zones-holder {
        flex: 1 1;
    }

    &__sortable-header {
        font-size: 14px;
        font-weight: 700;
        width: -webkit-fill-available;
        height: -webkit-fill-available;
        // color: $rfx-dark-march;
        color: #fff !important;
    }

    &__web-property {
        width: 200px;
        margin-right: 36px;
    }

    &__radio-wrap {
        height: 40px;
        display: flex;
        align-items: center;
        padding-left: 20px;

        .url__radio:first-child {
            margin-right: 10px;
        }
    }

    &__grid-select {
        width: 100px;
        margin-right: 25px;
    }

    &__loader {
        display: flex;
        width: 100%;
        justify-content: center;
        align-items: center;
        height: calc(100vh - 530px);
    }

    &__no-data {
        display: flex;
        height: 300px;
        background-color: #fff;
        border-radius: $rfx-default-border-radius;
        align-items: center;
        justify-content: center;
        font-size: 27px;
        font-weight: 600;
        color: $rfx-medium-grey;
        flex: 1 1;
    }

    &__table {
        position: relative;
        display: flex;
        flex-direction: column;
        overflow: visible !important;
    }

    &__update-loader {
        position: absolute;
        display: flex;
        width: 100%;
        height: 100%;
        justify-content: center;
        background-color: white;
        top: 0;
        opacity: 0.5;
        z-index: 10000;
    }

    &__update-loader-ring {
        margin-top: 200px;
    }

    &__ranking1,
    &__ranking2 {
        position: relative;
        width: 160px;
        flex: 0 0 160px;

        .url__select {
            position: absolute;
            left: 0;
            top: -50px;
            width: 140px;
            color: $rfx-dark-march;

            &_shifted {
                top: -110px;
            }
        }
    }

    &__change,
    &__changePercent,
    &__keywordTotal,
    &__totalMsv {
        width: 115px;
        flex: 0 0 115px;
    }

    &__change {
        &_uptrend {
            color: $rfx-green;
        }

        &_downtrend {
            color: $rfx-red;
        }
    }

    &__label {
        display: flex;
        align-items: center;
        padding-left: 2.4% !important;
        flex: 1 1;
        padding-right: 10px;
    }

    &__label-link {
        display: block;
        flex: 1 1;
        color: $rfx-blue;
    }

    &__label-icon {
        font-size: 16px !important;
        margin-right: 10px;
        margin-bottom: -3px;
    }

    &__url {
        color: $rfx-dark-march;

        &.common-table__header-cell {
            flex: 1 1;
            padding-left: 2.4%;
        }
    }

    &__advanced {
        margin-top: 30px;
        margin-bottom: 14px;
        position: relative;

        &::v-deep .collapsible__header {
            padding-right: 80px !important;
        }

        &::v-deep .collapsible__arrow {
            right: 66px !important;
        }
    }

    &__export-button {
        color: $rfx-bright-blue;
        position: absolute;
        right: 24px;
        top: 20px;
        z-index: 9;
        cursor: pointer;
        font-size: 20px;

        &_disabled {
            cursor: default;
            pointer-events: none;
            opacity: .4;
        }
    }

    .common-table {
        border-radius: 4px;
        min-height: 550px;
        background-color: $rfx-background-color;

        &::-webkit-scrollbar {
            visibility: hidden;
        }

        &__no-data {
            display: flex;
            height: 300px;
            background-color: #fff;
            border-radius: $rfx-default-border-radius;
            align-items: center;
            justify-content: center;
            font-size: 27px;
            font-weight: 600;
            color: $rfx-medium-grey;
        }

        &__row {
            background-color: #fff;
            border-left: 1px solid $rfx-light-steel;
            border-right: 1px solid $rfx-light-steel;

            &:last-child {
                border-bottom-right-radius: 4px;
                border-bottom-left-radius: 4px;
            }
        }

        &>div {
            &:last-child {
                border-bottom-left-radius: 4px;
                border-bottom-right-radius: 4px;
            }
        }

        &__summary {
            height: 60px;
            align-items: center;
            color: $rfx-light-march; 
            background-color: rgba(1, 22, 39, .1);
        }

        &__header {
            border-top-right-radius: 4px;
            border-top-left-radius: 4px;

            .common-table__sort-arrow {
                color: #fff;
            }
        }

        &__radio {
            margin-right: 15px;
            cursor: pointer;

            &::v-deep .radio__label {
                font-size: 14px;
                font-weight: normal;
            }
        }

        &__header-cell {
            // background-color: rgba(1, 22, 39, .1);
            background-color: #3b5162 !important;
            font-weight: bold;
            padding: 0;
            height: 60px;
            display: flex;
            align-items: center;
            cursor: pointer;

            &:first-child {
                border-left: 1px solid $rfx-light-steel;

                &::v-deep .multi-select__title-container {
                    padding-left: 6.6%;
                }
            }

            &:last-child {
                border-right: 1px solid $rfx-light-steel;
            }

            &::v-deep {
                .multi-select__title {
                    color: #fff !important;
                }

                .multi-select__arrow::after {
                    border-right-color: #fff;
                    border-bottom-color: #fff;
                }

                .multi-select_selected,
                .multi-select__header:hover {
                    background-color: $rfx-medium-march !important;
                }

                .multi-select__dropdown {
                    color: $rfx-dark-march;
                }

                .multi-select_highlighted {
                    display: none;
                }
            }
        }

        &__date_selection {
            border-top: 1px solid $rfx-light-steel;
            padding: 10px 0;
            border-top-right-radius: 4px;
            border-top-left-radius: 4px;
        }

        &__cell {
            height: 60px;
            display: flex;
            align-items: center;
            font-size: 14px;
            border-bottom: 1px solid $rfx-light-steel;
        }
        
        &__cell,
        &__summary-cell {
            padding: 0 0 0 16px;
            box-sizing: border-box;
        }
    }

    &::v-deep .common-table__sort-arrow {
        color: #fff;
    }
}

.no-data-block {
    border-bottom: 1px solid $rfx-light-steel;
}

.pagination-wrapper {
    display: flex;
    height: 60px;
    margin-top: 10px;
    justify-content: center;
    align-items: center;
    border: 1px solid $rfx-light-steel;
    border-radius: 4px;
}
</style>
